import React from 'react';
import ModalFormContainer from '../../components/forms/modalFormContainer/ModalFormContainer';
import {useAppContext} from '../../context/AppContext/AppContext';
import {isCallSwitch, isYay} from '../../helpers';
import csWhatsapp from '../../img/whats-new/callswitch/whatsapp.png';
import csAi from '../../img/whats-new/callswitch/ai.png';
import csQueue from '../../img/whats-new/callswitch/queue.png';
import phdWhatsapp from '../../img/whats-new/pulsehd/whatsapp.png';
import phdAi from '../../img/whats-new/pulsehd/ai.png';
import phdQueue from '../../img/whats-new/pulsehd/queue.png';
import yayWhatsapp from '../../img/whats-new/yay/whatsapp.png';
import yayAi from '../../img/whats-new/yay/ai.png';
import yayQueue from '../../img/whats-new/yay/queue.png';
import styles from './styles.module.scss';

// Midnight 1 Oct 2026 in the UK (BST, UTC+1), regardless of the user's timezone
const SWITCH_DEADLINE = new Date('2026-10-01T00:00:00+01:00');

const getDaysLeftToSwitch = (): number =>
    Math.max(0, Math.ceil((SWITCH_DEADLINE.getTime() - Date.now()) / 86400000));

export const hasSwitchDeadlinePassed = (): boolean => Date.now() >= SWITCH_DEADLINE.getTime();

const getBrandImages = (): {whatsapp: string; ai: string; queue: string} => {
    switch (true) {
        case isYay:
            return {whatsapp: yayWhatsapp, ai: yayAi, queue: yayQueue};
        case isCallSwitch:
            return {whatsapp: csWhatsapp, ai: csAi, queue: csQueue};
        default:
            return {whatsapp: phdWhatsapp, ai: phdAi, queue: phdQueue};
    }
};

const HERO_TAGS = [
    {label: 'WhatsApp Business Beta Access', color: '#35C759'},
    {label: 'AI Call Insights Beta Access', color: '#0A84FF'},
];

const FEATURES = [
    {label: 'Refreshed UI', color: '#35C759'},
    {label: 'Built for accessibility', color: '#FF3B30'},
    {label: 'Consistent across all screens', color: '#0A84FF'},
    {label: 'WhatsApp built in', color: '#35C759'},
    {label: 'New AI features', color: '#FF3B30'},
    {label: 'Faster system performance', color: '#0A84FF'},
];

interface Props {
    showModal: boolean;
    setShowModal: (val: boolean) => void;
    appName: string;
    onSecondaryClick?: () => void;
    forced?: boolean;
}

const WhatsNewModal: React.FC<Props> = ({
    showModal,
    setShowModal,
    appName,
    onSecondaryClick,
    forced,
}) => {
    const {newAppDownloadURL} = useAppContext();
    const brandImages = getBrandImages();
    const daysLeft = getDaysLeftToSwitch();

    return (
        <ModalFormContainer
            showModal={showModal}
            setShowModal={setShowModal}
            preventClose={forced}
        >
            <div className={styles.whats_new}>
                <div className={styles.hero}>
                    <div className={styles.hero_top}>
                        <h3>See what&apos;s new</h3>
                        {!forced ? (
                            <button
                                type='button'
                                aria-label='Close'
                                onClick={() => setShowModal(false)}
                            >
                                <i className='icon-cross'/>
                            </button>
                        ) : null}
                    </div>
                    <div className={styles.hero_content}>
                        <img src={brandImages.whatsapp} alt='WhatsApp'/>
                        <img src={brandImages.ai} alt='AI Call Insights'/>
                        <img src={brandImages.queue} alt='Queue Management'/>
                    </div>
                    <ul className={styles.hero_tags}>
                        {HERO_TAGS.map(tag => (
                            <li key={tag.label}>
                                <span style={{background: tag.color}}/>
                                {tag.label}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className={styles.body}>
                    <p className={styles.badge}>
                        {forced
                            ? 'The switch deadline has passed'
                            : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left to switch`}
                    </p>
                    <h2>The redesigned {appName} desktop app is here</h2>
                    <p className={styles.description}>
                        A cleaner interface, WhatsApp Business messaging built in, and AI that
                        helps you move faster, with a smoother and more efficient experience...
                    </p>
                    <ul className={styles.features}>
                        {FEATURES.map(feature => (
                            <li key={feature.label}>
                                <span style={{background: feature.color}}/>
                                {feature.label}
                            </li>
                        ))}
                    </ul>
                    <div className={styles.footer}>
                        <button
                            type='button'
                            className={styles.primary_button}
                            onClick={() => {
                                if (newAppDownloadURL) {
                                    window.open(newAppDownloadURL, '_blank');
                                }
                            }}
                        >
                            Download now
                        </button>
                        {!forced ? (
                            <button
                                type='button'
                                className={styles.secondary_button}
                                onClick={onSecondaryClick}
                            >
                                Remind me tomorrow
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </ModalFormContainer>
    );
};

export default WhatsNewModal;
